//BMC
import React, { Suspense, lazy } from 'react'
import { connect } from 'react-redux'
import { Route, Routes, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Card, Loader } from 'semantic-ui-react'
import { isIE } from 'globals/utils'

import logo from 'img/logo.svg'

import Navigation from 'components/layout/navigation'

import LoginPage from 'scenes/user/login'
import LoadingPage from 'scenes/user/loading'
import ErrorPage from 'scenes/error'
import TimeoutPage from 'scenes/timeout'
import RolloverPage from 'scenes/apps/bmc/rollover'
import WhatsNewPage from 'scenes/whatsnew'
import SubErrorPage from 'scenes/user/subscription/error'

const ToursPage = lazy(() => import(/* webpackChunkNmae: "tour" */ 'scenes/apps/bmc/tours'))

const ForgotPasswordPage = lazy(() => import(/* webpackChunkName: "security" */ 'scenes/user/password/forgot')) 
const ResetPasswordSentPage = lazy(() => import(/* webpackChunkName: "security" */ 'scenes/user/password/sent')) 
const ResetPasswordPage = lazy(() => import(/* webpackChunkName: "security" */ 'scenes/user/password/reset')) 
const LostDevicePage = lazy(() => import(/* webpackChunkName: "security" */ 'scenes/user/login/lostDevice')) 

const SignupPage = lazy(() => import(/* webpackChunkName: "signup" */ 'scenes/user/signup'))
const AcceptInvitationPage = lazy(() => import(/* webpackChunkName: "signup" */ 'scenes/user/signup/accept'))
const ConfirmationPage = lazy(() => import(/* webpackChunkName: "signup" */ 'scenes/user/confirmation'))

const FAQPage = lazy(() => import(/* webpackChunkName: "user" */ 'scenes/faq'))
const ChangelogPage = lazy(() => import(/* webpackChunkName: "user" */ 'scenes/changelog'))
const SettingsPage = lazy(() => import(/* webpackChunkName: "user" */ 'scenes/user/settings'))
const PaymentCancelPage = lazy(() => import(/* webpackChunkName: "user" */ 'scenes/user/subscription/cancel'))
const PaymentCompletePage = lazy(() => import(/* webpackChunkName: "user" */ 'scenes/user/subscription/payment'))
const SubscriptionHandler = lazy(() => import(/* webpackChunkName: "user" */ 'scenes/user/subscription'))

const DashboardPage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/dashboard'))
const PropertiesPage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/properties'))
const SavingsPage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/savings'))
const IncomePage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/income'))
const DebtsPage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/debts'))
const LifestyleExpensesPage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/lifestyle'))
const ActivitiesPage = lazy(() => import(/* webpackChunkName: "thisyear" */ 'scenes/apps/bmc/thisyear/activities'))

const PlanPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/plan'))
const PlanEventsPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/events'))
const PlanPropertyPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/property'))
const PlanSavingsPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/savings'))
const PlanIncomePage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/income'))
const PlanExpensesPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/expenses'))
const PlanVacationPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/activities/PlanVacationPage'))
const PlanRenovationsPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/activities/PlanRenovationsPage'))
const PlanActivitiesPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/activities/PlanActivitiesPage'))
const PlanDebtsPage = lazy(() => import(/* webpackChunkName: "plan" */ 'scenes/apps/bmc/futurechoices/debts'))

const WizardPage = lazy(() => import(/* webpackChunkName: "wizard" */ 'scenes/wizard'))
const WizardSubPage = lazy(() => import(/* webpackChunkName: "wizard" */ 'scenes/wizardsub'))

const ReportsPage = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports'))
const ReportChoices = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/choices'))

const ReportNetworthOverview = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/networth/overview'))
const ReportNetworthProperty = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/networth/property'))
const ReportNetworthSavings = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/networth/savings'))
const ReportNetworthDebt = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/networth/debt'))

const ReportUsesOverview = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/uses/overview'))
const ReportUsesExpenses = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/uses/expenses'))
const ReportUsesSavings = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/uses/savings'))

const ReportSourcesOverview = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/sources/overview'))
const ReportSourcesIncomes = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/sources/incomes'))
const ReportSourcesSavings = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/charts/sources/savings'))

const ReportSurplusDeficit = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/surplusdeficit'))
const ReportPlanAudit = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/exports/plan_audit'))
const ReportSourcesUses = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/tables/sourcesuses'))
const ReportSources = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/tables/sources'))
const ReportUses = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/tables/uses'))
const ReportBalanceSheet = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/tables/balancesheet'))
const ReportTaxSheet = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/tables/taxes'))
const ReportDetailed = lazy(() => import(/* webpackChunkName: "reports" */ 'scenes/apps/bmc/reports/tables/detailed'))

const CompareNetworth = lazy(() => import(/* webpackChunkName: "compare" */ 'scenes/apps/bmc/reports/compare/networth'))
const CompareTaxes = lazy(() => import(/* webpackChunkName: "compare" */ 'scenes/apps/bmc/reports/compare/taxes'))

const AdvisorSettingsPage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/settings'))
const AdvisorDashboardPage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/dashboard'))
//const AdvisorClientEditPage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/client'))
const AdvisorBMCPage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/client/bmc'))
const AdvisorClientInvitePage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/client/invite'))
const AdvisorClientCreatePage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/client/create'))
const AdvisorClientSettingsPage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/client/settings'))
const AdvisorDrawdownPage = lazy(() => import(/* webpackChunkName: "advisor" */ 'scenes/advisor/drawdown'))

const AdminAdvisorsPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/advisors'))
const AdminAdvisorPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/advisor'))
const AdminUsersPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/users'))
const AdminSystemPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/system'))
const AdminTaxPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/taxtables'))
const AdminChangeLogPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/changelog'))
const AdminSettingsPage = lazy(() => import(/* webpackChunkName: "admin" */ 'scenes/admin/settings'))
const AdminNavigation = lazy(() => import(/* webpackChunkName: "admin" */ 'components/layout/navigationadmin'))

const AdminRoutes = [
  {
    path: '/admin',
    element: <AdminAdvisorsPage />
  },{
    path: '/admin/advisor',
    element: <AdminAdvisorPage />
  },{
    path: '/admin/users',
    element: <AdminUsersPage />
  },{
    path: '/admin/system',
    element: <AdminSystemPage />
  },{
    path: '/admin/taxes',
    element: <AdminTaxPage />
  },{
    path: '/admin/change',
    element: <AdminChangeLogPage />
  },{
    path: '/admin/settings',
    element: <AdminSettingsPage />
  }
]

const AdvisorRoutes = [
  {
    path: '/advisor',
    element: <AdvisorDashboardPage />
  },{
    path: '/advisor/settings',
    element: <AdvisorSettingsPage />
  },{
    path: '/advisor/client',
    element: <AdvisorBMCPage />
  },{
    path: '/advisor/client/create',
    element: <AdvisorClientCreatePage />
  },{
    path: '/advisor/client/settings',
    element: <AdvisorClientSettingsPage />
  },{
    path: '/advisor/client/invite',
    element: <AdvisorClientInvitePage />
  },{
    path: '/advisor/drawdown',
    element: <AdvisorDrawdownPage />
  }
]

const BMCRoutes = [{
  path: '/bmc/thisyear',
  element: <DashboardPage />
},{
  path: '/bmc/savings',
  element: <SavingsPage />
},{
  path: '/bmc/debts',
  element: <DebtsPage />
},{
  path: '/bmc/income',
  element: <IncomePage />
},{
  path: '/bmc/lifestyle',
  element: <LifestyleExpensesPage />
},{
  path: '/bmc/properties',
  element: <PropertiesPage />
},{
  path: '/bmc/activities',
  element: <ActivitiesPage />
},{
  path: '/bmc/events',
  element: <PlanEventsPage />
},{
  path: '/bmc/future',
  element: <PlanPage />
},{
  path: '/bmc/future/property',
  element: <PlanPropertyPage />
},{
  path: '/bmc/future/savings',
  element: <PlanSavingsPage />
},{
  path: '/bmc/future/expenses',
  element: <PlanExpensesPage />
},{
  path: '/bmc/future/vacations',
  element: <PlanVacationPage />
},{
  path: '/bmc/future/renovations',
  element: <PlanRenovationsPage />
},{
  path: '/bmc/future/activities',
  element: <PlanActivitiesPage />
},{
  path: '/bmc/future/income',
  element: <PlanIncomePage />
},{
  path: '/bmc/future/debts',
  element: <PlanDebtsPage />
},{
  path: '/bmc/reports/compare/networth',
  element: <CompareNetworth />
},{
  path: '/bmc/reports/compare/taxes',
  element: <CompareTaxes />  
},{
  path: '/bmc/reports/choices',
  element: <ReportChoices />
},{
  path: '/bmc/reports/plan_audit',
  element: <ReportPlanAudit />
},{
  path: '/bmc/reports/networth/overview',
  element: <ReportNetworthOverview />  
},{
  path: '/bmc/reports/networth/property',
  element: <ReportNetworthProperty />
},{
  path: '/bmc/reports/networth/savings',
  element: <ReportNetworthSavings />
},{
  path: '/bmc/reports/networth/debt',
  element: <ReportNetworthDebt />
},{
  path: '/bmc/reports/sources/overview',
  element: <ReportSourcesOverview />
},{
  path: '/bmc/reports/sources/incomes',
  element: <ReportSourcesIncomes />
},{
  path: '/bmc/reports/sources/savings',
  element: <ReportSourcesSavings />
},{
  path: '/bmc/reports/uses/overview',
  element: <ReportUsesOverview />
},{
  path: '/bmc/reports/uses/expenses',
  element: <ReportUsesExpenses />
},{
  path: '/bmc/reports/uses/savings',
  element: <ReportUsesSavings />
},{
  path: '/bmc/reports/surplusdeficit',
  element: <ReportSurplusDeficit />
},{
  path: '/bmc/reports/sources',
  element: <ReportSources />
},{
  path: '/bmc/reports/uses',
  element: <ReportUses />
},{
  path: '/bmc/reports/sourcesuses',
  element: <ReportSourcesUses />
},{
  path: '/bmc/reports/balancesheet',
  element: <ReportBalanceSheet />
},{
  path: '/bmc/reports/taxes',
  element: <ReportTaxSheet />
},{
  path: '/bmc/reports/detailed',
  element: <ReportDetailed />
},{
  path: '/bmc/reports',
  element: <ReportsPage />
},{
  path: '/bmc/tours',
  element: <ToursPage />
},{
  path: '/bmc/rollover',
  element: <RolloverPage />
}
]

function App({ isAuthenticated, status }) {
  const location = useLocation()
  const noNav = ['/login','/rollover','/whatsnew','/wizard','/wizardsub','/subscription']
  if (isIE(navigator)) {
    return (
      <div className='ui container' style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginTop: '60px'}}>
        <img alt='ei_logo' src={logo} width='290'/>
        <Card>
          <Card.Content>
            <p>We have detected that you are using Internet Explorer, you may experience difficulties as IE is no longer being updated.</p>
            <p>Please trying using Google Chrome, Firefox, Safari, Edge or another up to date browser.</p>
            <p>Thank you and sorry for the inconvenience!</p>
          </Card.Content>
        </Card>
      </div>
    )
  }
  return (
    <div className="App"> 
      <div className="app_wrap">     
        <div style={{left: '0', top: '0', position: 'absolute', color: process.env.REACT_APP_VERSION_COLOR}}>Build [{process.env.REACT_APP_BUILD}]</div>      
        { isAuthenticated && ['expired','notrial','paymenterror'].includes(status) ?
          <Suspense fallback={<Loader active inverted>Loading</Loader>}>
            <Routes>
              <Route path="/login" exact element={<LoginPage/>} /> 
              <Route path="/suberror" exact element={<SubErrorPage/>} />
              <Route path="/subscription" exact element={<SubscriptionHandler/>} />
              <Route path='*' element={<Navigate to='/suberror'/>}/>
            </Routes>
          </Suspense>
          :
          isAuthenticated && status === 'new' ?
          <Suspense fallback={<Loader active inverted>Loading</Loader>}>
            <Routes> 
              <Route path="/login" exact element={<LoginPage/>} /> 
              <Route path="/wizard" exact element={<WizardPage/>} />
              <Route path='*' element={<Navigate to='/wizard'/>}/>
            </Routes>
          </Suspense>
          :
          <Suspense fallback={<Loader active inverted>Loading</Loader>}>
            { isAuthenticated && !noNav.includes(location.pathname) && (status === 'admin' ?
              <AdminNavigation/>
              :
              <Navigation />)
            }
            <Routes>
              <Route path="/login" exact element={<LoginPage/>} />
              <Route path="/subscription" exact element={<SubscriptionHandler/>} />
              <Route path="/loading" exact element={<LoadingPage/>} />
              <Route path="/error" exact element={<ErrorPage/>} />
              <Route path="/timeout" exact element={<TimeoutPage/>} />
              <Route path="/signup" exact element={<SignupPage/>} />
              <Route path="/acceptinvitation" exact element={<AcceptInvitationPage/>} />
              <Route path="/confirmation" exact element={<ConfirmationPage/>} />
              <Route path="/forgotpassword" exact element={<ForgotPasswordPage/>} />
              <Route path="/resetpassword" exact element={<ResetPasswordPage/>} />
              <Route path="/resetsent" exact element={<ResetPasswordSentPage/>} />
              <Route path="/lostDevice" exact element={<LostDevicePage/>} />
              <Route element={<RequireAuth userAuth={isAuthenticated}/>}>
                <Route path="/settings" exact element={<SettingsPage/>} />
                <Route path='/faq' exact element={<FAQPage/>} />
                <Route path="/changelog" exact element={<ChangelogPage/>} />
                <Route path="/rollover" exact element={<RolloverPage/>} />
                <Route path="/whatsnew" exact element={<WhatsNewPage/>} />
                <Route path="/wizard" exact element={<WizardPage/>} />
                <Route path="/wizardsub" exact element={<WizardSubPage />} />
                <Route path="/paymentcancel" exact element={<PaymentCancelPage />} />
                <Route path="/paymentcomplete" exact element={<PaymentCompletePage />} />
                {
                  AdminRoutes.map((v,i)=> <Route key={`authed-route-key-${i}`} exact path={v.path} element={v.element} />)
                }
                {
                  AdvisorRoutes.map((v,i)=> <Route key={`authed-route-key-${i}`} exact path={v.path} element={v.element} />)
                } 
                {
                  BMCRoutes.map((v,i)=> <Route key={`authed-route-key-${i}`} exact path={v.path} element={v.element} />)
                } 
              </Route>
              <Route path="/suberror" element={<Navigate to='/whatsnew'/>} />
              <Route path='*' element={<Navigate to='/login'/>}/>
            </Routes>
          </Suspense>
        }
      </div>
      <div class='app_footer'>
        <div class='app_footer_container'>
          <div class='app_footer_content'>
            <p class='app_footer_link'>©Better Money Choices 2020. All rights reserved.</p>
            <div class='app_footer_divider'></div>
            <a href='https://bettermoneychoices.com/privacy.html' target='_blank' class='app_footer_link'>Privacy Policy</a>
            <div class='app_footer_divider'></div>
            <a href='https://bettermoneychoices.com/terms.html' target='_blank' class='app_footer_link'>Terms of Use</a>
            <div class='app_footer_divider'></div>
            <a href='https://bettermoneychoices.com/about.html#contactus' target='_blank' class='app_footer_link'>Contact Us</a>
          </div>
        </div>
      </div>
    </div>
  )
}

function RequireAuth({userAuth}) { 
  if (!userAuth) {
    return <Navigate to="/login" />
  }
  return <Outlet />
}

function mapStateToProps(state){
  return{
    isAuthenticated: !!state.auth.token, 
    status: state.auth.status
  }
}

export default connect(mapStateToProps)(App)